import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import type { DiscordWebhook } from '../../types/discord';

interface Props {
  open: boolean;
  webhook: DiscordWebhook;
  onClose: () => void;
  testFn: (content: string) => Promise<{ success: boolean; error?: string }>;
}

export function TestWebhookModal({ open, webhook, onClose, testFn }: Props) {
  const [content, setContent] = useState('Test message from WebAdmin');
  const [result, setResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [sending, setSending] = useState(false);

  function handleClose() {
    setResult(null);
    onClose();
  }

  async function handleSend() {
    setSending(true);
    setResult(null);
    try {
      const r = await testFn(content);
      setResult(r);
    } catch {
      setResult({ success: false, error: 'Request failed' });
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Test Webhook — {webhook.name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Send a test message to {webhook.channel} on {webhook.server}.
        </Typography>
        <TextField
          label="Message"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          size="small"
        />
        {result && (
          <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 2 }}>
            {result.success ? 'Message sent successfully.' : result.error ?? 'Unknown error'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button variant="contained" onClick={handleSend} disabled={!content.trim() || sending}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}
